

import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import React, { lazy, Suspense, useLayoutEffect, useState, useEffect } from 'react';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import 'react-modern-drawer/dist/index.css';
import 'react-photo-view/dist/react-photo-view.css';
import 'react-quill/dist/quill.snow.css';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import './assets/Styles/style.css';

// Components
import FloatingChatBot from './Component/FloatingChatBot';
import { Loader } from './Component/Loader';
import AgentDetailPage from './pages/AgentDetailPage/AgentDetailPage';
import AgentOrCompany from './pages/AgentOrCompany';
import Blogs from './pages/Blogs';
import CompanyDetailPage from './pages/CompanyDetailPage/CompanyDetailPage';
import EventDetail from './pages/EventDetails';
import ForgotPassword from './pages/ForgotPassword';
import NewEvents from './pages/NewEvents';
import PropertyDetails from './pages/PropertyDetail';

import ScrollToTop from './Helper/ScrollToTop';
import AccountSettings from './pages/AccountSettings';
import AgentDashboard from './pages/Agent/Dashboard';
import AgentCompanyLogin from './pages/AgentCompanyLogin/AgentCompanyLogin';
import AgentRegisterationForm from './pages/AgentRegisterationForm';
import BlogDetail from './pages/BlogDetail';
import ChatBot from './pages/ChatBot';
import CompanyDashboard from './pages/CompanyDashboard';
import CompareList from './pages/CompareList';
import ContactUs from './pages/ContactUs';
import FollowedAgents from './pages/FollowedAgents';
import Login from './pages/Login';
import MortgageLoanPage from './pages/MortgageLoanPage';
import NotificationPage from './pages/Notification';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ProjectDetail from './pages/ProjectDetail';
import Projects from './pages/Projects';
import Properties from './pages/Properties';
import PropertyRequest from './pages/PropertyRequest';
import SavedProperties from './pages/SavedProperties';
import SavedSearches from './pages/SavedSearches';
import Signup from './pages/Signup';
import TermsAndConditions from './pages/TermsAndConditions';
import VerifyOtp from './pages/VerifyOtp';
const LandingPage = lazy(() => import('./pages/LandingPage/LandingPage'));
import AgentEventsManagement from './pages/Agent/EventsManagement';
import AgentFollowers from './pages/Agent/Followers';
import AgentCreateNewEvent from './pages/Agent/NewEvent';
import AgentCreateNewProject from './pages/Agent/NewProject';
import AgentCreateNewProperty from './pages/Agent/NewProperty';
import ProfileSettings from './pages/Agent/ProfileSettings';
import AgentProjectsManagement from './pages/Agent/ProjectsManagement';
import AgentPropertiesManagement from './pages/Agent/PropertiesManagement';
import PropertyRequests from './pages/Agent/PropertyRequests';
import CompanyCreateNewProperty from './pages/Company/NewProperty';
import CompanyPropertiesManagement from './pages/Company/PropertiesManagement';
import AddAgentPage from './pages/Company/AddAgentPage';
import CompanyAgentsManagement from './pages/Company/AgentsManagement';
import CompanyEventsManagement from './pages/Company/EventsManagement';
import CompanyFollowers from './pages/Company/Followers';
import CompanyCreateNewEvent from './pages/Company/NewEvent';
import CompanyCreateNewProject from './pages/Company/NewProject';
import CompanyProfileSettings from './pages/Company/ProfileSettings';
import CompanyProjectsManagement from './pages/Company/ProjectsManagement';
import CompanyPropertyRequests from './pages/Company/PropertyRequests';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Get } from './Axios/AxiosFunctions';
import { BaseURL } from './config/apiUrl';
import { languagesOptions } from './constant/commonData';
import { EnglishLanguage } from './constant/imagePath';
import { useSocket } from './context/SocketContext';
import BeforeLoginRoute from './Helper/BeforeLoginRoute';
import ProtectedRoute from './Helper/ProtectedRoute';
import ProjectUnit from './pages/Company/ProjectUnit';
import UserContactedProperties from './pages/ContactedProperties';
import OTPSignup from './pages/OTPSignup/OTPSignup';
import UserPropertyRequests from './pages/PropertyRequests';
import ResetPassword from './pages/ResetPassword';
import {
  useLazyGetAllAmenitiesQuery,
  useLazyGetAllBannerQuery,
  useLazyGetAllEventTypeQuery,
  useLazyGetAllProjectStatusesQuery,
  useLazyGetAllProjectTypeQuery,
  useLazyGetAllPropertyTypeQuery,
  useLazyGetAllUnitTypeQuery,
  useLazyGetApiQuery,
  useLazyGetCMSPagesQuery,
  useLazyGetCompanyAgentsQuery,
  useLazyGetContactDetailsQuery,
  useLazyGetLanguagesQuery,
  useLazyGetReportReasonsQuery
} from './store/apiSlice/apiSlice';
import { updateUser } from './store/auth/authSlice';
import {
  setAllAgents,
  setAllAmenities,
  setAllBanners,
  setAllCMSPages,
  setAllCurrency,
  setAllEventTypes,
  setAllLanguages,
  setAllProjectTypes,
  setAllPropertyTypes,
  setContactDetails,
  setProjectStatus,
  setReportReasons,
  setUnitTypes
} from './store/common/commonSlice';
import { setDirection, setSelectLanguage } from './store/language/languageSlice';

function App() {
  const socket = useSocket();
  const { ready, i18n } = useTranslation();
  const { isLogin, accessToken, user } = useSelector((state) => state?.authReducer);
  const { selectedLanguage, direction } = useSelector((state) => state?.languageReducer);
  const { languages } = useSelector((state) => state.commonReducer);
  const mapLang = languages && languages;
  useEffect(() => {
    const cssId = 'dynamic-css';
    const existingLink = document.getElementById(cssId);

    // Remove previously injected CSS
    if (existingLink) {
      existingLink.remove();
    }

    // Determine correct CSS file
    const href = ['ar', 'fa'].includes(selectedLanguage) ? '/indexArabic.css' : '/index.css';

    // Inject the new CSS
    const link = document.createElement('link');
    link.id = cssId;
    link.rel = 'stylesheet';
    link.href = href;

    document.head.appendChild(link);

    return () => {
      const current = document.getElementById(cssId);
      if (current) current.remove();
    };
  }, [selectedLanguage]);

  const [getAllEventsTypes] = useLazyGetAllEventTypeQuery();
  const [getAllProjectStatuses] = useLazyGetAllProjectStatusesQuery();
  const [getAllUnitTypes] = useLazyGetAllUnitTypeQuery();
  const [getAllPropertiesTypes] = useLazyGetAllPropertyTypeQuery();
  const [getAllProjectsTypes] = useLazyGetAllProjectTypeQuery();
  const [getAllAmenities] = useLazyGetAllAmenitiesQuery();
  const [getCompanyAgents] = useLazyGetCompanyAgentsQuery();
  const [getAllBanners] = useLazyGetAllBannerQuery();
  const [getAllCurrency] = useLazyGetApiQuery();
  const [getAllContactDetails] = useLazyGetContactDetailsQuery();
  const [getAllReportReasons] = useLazyGetReportReasonsQuery();
  const [getAllCMSPages] = useLazyGetCMSPagesQuery();
  const [getAllLanguages] = useLazyGetLanguagesQuery();
  const dispatch = useDispatch();

  const [isLoadingCmdPages, setIsLoadingCmdPages] = useState(true);

  const getData = async () => {
    const [
      allsLanguagesData,
      cmsPagesData,
      dataForEventType,
      dataForPropertyType,
      dataForProjectType,
      dataForInteriorAmenities,
      dataForExteriorAmenities,
      companyAgents,
      allBanners,
      allCurrency,
      contactDetails,
      reportReasons,
      projectStatuses,
      unitTypes
    ] = await Promise.allSettled([
      getAllLanguages(),
      getAllCMSPages(),
      getAllEventsTypes(),
      getAllPropertiesTypes(),
      getAllProjectsTypes(),
      getAllAmenities('interior'),
      getAllAmenities('exterior'),
      // user?.role == 'company' && getCompanyAgents(),

      user?.role == 'company' && getCompanyAgents(),
      getAllBanners(),
      getAllCurrency({
        url: `currency`,
        tag: `Currency`
      }),
      getAllContactDetails(),
      getAllReportReasons(),
      getAllProjectStatuses(),
      getAllUnitTypes()
    ]);
  
    if (allsLanguagesData?.value?.data?.data?.languages) {
      const activeLanguages = allsLanguagesData.value.data.data.languages
        .filter((lang) => lang.isActive)
        .map((lang) => {
          const option = languagesOptions.find((opt) => opt.value === lang.language);
          return option ? option : { label: lang.language, value: lang.language };
        });
      dispatch(
        setAllLanguages(
          activeLanguages.length
            ? activeLanguages
            : { label: 'English', value: 'en', flag: EnglishLanguage }
        )
      );
    }

    if (cmsPagesData?.value?.data) {
      dispatch(setAllCMSPages(cmsPagesData?.value?.data?.data));
      setIsLoadingCmdPages(false);
    }
    if (dataForEventType?.value?.data) {
      dispatch(setAllEventTypes(dataForEventType?.value?.data));
    }
    if (dataForPropertyType?.value?.data) {
      dispatch(setAllPropertyTypes(dataForPropertyType?.value?.data));
    }
    if (dataForProjectType?.value?.data) {
      dispatch(setAllProjectTypes(dataForProjectType?.value?.data));
    }
    if (dataForInteriorAmenities?.value?.data) {
      dispatch(
        setAllAmenities({ data: dataForInteriorAmenities?.value?.data?.data, type: 'interior' })
      );
    }
    if (dataForExteriorAmenities?.value?.data) {
      dispatch(
        setAllAmenities({ data: dataForExteriorAmenities?.value?.data?.data, type: 'exterior' })
      );
    }
    if (companyAgents?.value?.data) {
      dispatch(setAllAgents({ data: companyAgents?.value?.data?.data || [] }));
    }
    if (allBanners?.value?.data) {
      dispatch(setAllBanners({ data: allBanners?.value?.data?.data }));
    }
    if (allCurrency?.value?.data) {
      dispatch(setAllCurrency({ data: allCurrency?.value?.data?.data }));
    }
    if (contactDetails?.value?.data) {
      const data = contactDetails?.value?.data?.data;

      dispatch(
        setContactDetails({
          email: data?.email,
          phone: data?.contact?.startsWith('+') ? data?.contact : `+${data?.contact}`,
          whatsapp: data?.whatsapp?.startsWith('+') ? data?.whatsapp : `+${data?.whatsapp}`
        })
      );
    }
    if (reportReasons?.value?.data) {
      dispatch(setReportReasons(reportReasons?.value?.data?.data));
    }
    if (projectStatuses?.value?.data) {
      dispatch(setProjectStatus(projectStatuses?.value?.data?.data));
    }
    if (unitTypes?.value?.data) {
      dispatch(setUnitTypes(unitTypes?.value?.data?.data));
    }
  };

  const getUserData = async () => {
    const userData = BaseURL(`users/me`);
    const response = await Promise.allSettled([Get(userData, accessToken)]);
    if (response[0]?.status === 'fulfilled') {
      dispatch(updateUser(response?.[0]?.value?.data?.data?.user));
    }
  };

  useEffect(() => {
  
    getData();
   
  }, []);

  useEffect(() => {
    if (mapLang?.length > 0) {
      const language = mapLang?.find((l) => l.value === selectedLanguage)?.value || 'en';

      if (language) {
        const lng = language;
        i18n.changeLanguage(lng);
        dispatch(setSelectLanguage(lng));
        if (['ar', 'fa'].includes(lng)) {
          dispatch(setDirection('rtl'));
        } else {
          dispatch(setDirection('ltr'));
        }
      }
    }
  }, [mapLang]);

  useEffect(() => {
    if (isLogin) {
      getUserData();

      const userLang =
        mapLang?.find((l) => l.label === user?.preferredLanguage)?.label || 'English';

      if (userLang) {
        const language = mapLang?.find((l) => l.label?.toLowerCase() === userLang?.toLowerCase());

        if (language) {
          const lng = language?.value;
          i18n.changeLanguage(lng);
          dispatch(setSelectLanguage(lng));
          if (['ar', 'fa'].includes(lng)) {
            dispatch(setDirection('rtl'));
          } else {
            dispatch(setDirection('ltr'));
          }
        }
      }
    }
  }, [accessToken]);

  // For Language
  useLayoutEffect(() => {
 
    if (direction) {
      document.querySelector('body').dir = direction;
    }
    if (selectedLanguage) {
      i18n.changeLanguage(selectedLanguage);
    }
  }, [direction, selectedLanguage]);

  useEffect(() => {
    if (isLogin) {
      socket?.current?.on('package-expired', (user) => {
        dispatch(updateUser(user));
      });

      socket?.current?.on('package-purchased', (user) => {
        dispatch(updateUser(user));
      });
    }
  }, [isLogin]);

  // For Language

  if (!ready || isLoadingCmdPages) return <Loader />;

  return (
    <React.Fragment>
      <ToastContainer />
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <ScrollToTop />
          <Routes>
            <Route path="/" exact element={<LandingPage />} />
            <Route path="/login" exact element={<BeforeLoginRoute element={Login} />} />
            <Route
              path="/agent-company-login"
              exact
              element={<BeforeLoginRoute element={AgentCompanyLogin} />}
            />
            <Route path="/agents/:slug" exact element={<AgentDetailPage />} />
            <Route path="/company/:slug" exact element={<CompanyDetailPage />} />
            <Route path="/property/:slug" exact element={<PropertyDetails />} />
            <Route path="/projects/:slug" exact element={<ProjectDetail />} />
            <Route path="/events/:slug" exact element={<EventDetail />} />
            <Route path="/events" exact element={<NewEvents />} />
            <Route path="/agents" exact element={<AgentOrCompany key={'Agent'} />} />
            <Route path="/company" exact element={<AgentOrCompany key={'Company'} />} />
            <Route path="/blog" exact element={<Blogs />} />
            <Route path="/blog/:slug" exact element={<BlogDetail />} />
            <Route path="/projects" exact element={<Projects />} />
            <Route path="/buy" exact element={<Properties key={'buy'} purpose={'buy'} />} />
            <Route path="/rent" exact element={<Properties key={'rent'} purpose={'rent'} />} />
            <Route path="/property-request" exact element={<PropertyRequest />} />
            <Route path="/chat-bot" exact element={<ChatBot />} />
            <Route path="/advertise-with-us" exact element={<AgentRegisterationForm />} />
            {/* New Pages */}
            <Route path="/contact-us" exact element={<ContactUs />} />
            <Route path="/privacy-policy" exact element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" exact element={<TermsAndConditions />} />
            <Route path="/signup" exact element={<BeforeLoginRoute element={Signup} />} />
            <Route
              path="/forgot-password"
              exact
              element={<BeforeLoginRoute element={ForgotPassword} />}
            />
            <Route path="/verify-otp" exact element={<BeforeLoginRoute element={VerifyOtp} />} />
            <Route path="/verify-otp-signup" exact element={<OTPSignup />} />
            <Route
              path="/reset-password"
              exact
              element={<BeforeLoginRoute element={ResetPassword} />}
            />

            <Route path="/mortgage-bank-loan" exact element={<MortgageLoanPage />} />
            {/* Common Routes */}
            {/* <Route path="/dashboard" exact element={<UserDashboard />} /> */}
            {/* Common Routes */}

            {/* Agent Dashboard Routes */}
            <Route path="/agent" element={<ProtectedRoute allowedRole={['agent']} />}>
              <Route path="dashboard" exact element={<AgentDashboard />} />
              <Route path="account-settings" exact element={<ProfileSettings />} />

              <Route path="properties-management" exact element={<AgentPropertiesManagement />} />
              <Route
                path="properties-management/new-property"
                exact
                element={<AgentCreateNewProperty />}
              />
              <Route
                path="properties-management/edit-property/:slug"
                exact
                element={<AgentCreateNewProperty />}
              />
              <Route path="projects-management" exact element={<AgentProjectsManagement />} />
              <Route
                path="projects-management/new-project"
                exact
                element={<AgentCreateNewProject />}
              />
              <Route
                path="projects-management/edit-project/:slug"
                exact
                element={<AgentCreateNewProject />}
              />

              <Route path="projects-units/:id" exact element={<ProjectUnit />} />
              <Route path="events-management" exact element={<AgentEventsManagement />} />
              <Route path="events-management/new-event" exact element={<AgentCreateNewEvent />} />

              <Route
                path="events-management/edit-event/:slug"
                exact
                element={<AgentCreateNewEvent />}
              />

              <Route path="property-requests" exact element={<PropertyRequests />} />
              <Route path="followers" exact element={<AgentFollowers />} />
            </Route>
            {/* Agent Dashboard Routes */}

            {/* Company Dashboard Routes */}
            <Route path="/company" element={<ProtectedRoute allowedRole={['company']} />}>
              <Route path="dashboard" exact element={<CompanyDashboard />} />
              <Route path="agents-management/new-agent" exact element={<AddAgentPage />} />
              <Route path="agents-management/edit-agent/:slug" exact element={<AddAgentPage />} />

              <Route path="profile-settings" exact element={<CompanyProfileSettings />} />
              <Route path="agents-management" exact element={<CompanyAgentsManagement />} />

              <Route path="properties-management" exact element={<CompanyPropertiesManagement />} />
              <Route path="projects-units/:id" exact element={<ProjectUnit />} />

              <Route path="projects-management" exact element={<CompanyProjectsManagement />} />

              <Route path="events-management" exact element={<CompanyEventsManagement />} />
              <Route path="property-requests" exact element={<CompanyPropertyRequests />} />
              <Route path="followers" exact element={<CompanyFollowers />} />

              {/* This will replace */}
              <Route path="events-management/new-event" exact element={<CompanyCreateNewEvent />} />
              <Route
                path="events-management/edit-event/:slug"
                exact
                element={<CompanyCreateNewEvent />}
              />
              <Route
                path="properties-management/new-property"
                exact
                element={<CompanyCreateNewProperty />}
              />
              <Route
                path="properties-management/edit-property/:slug"
                exact
                element={<CompanyCreateNewProperty />}
              />
              <Route
                path="projects-management/new-project"
                exact
                element={<CompanyCreateNewProject />}
              />
              <Route
                path="projects-management/edit-project/:slug"
                exact
                element={<CompanyCreateNewProject />}
              />
              {/* This will replace */}
            </Route>
            {/* Company Dashboard Routes */}

            {/* <Route path="/following-agents" exact element={<FollowingAgents />} /> */}
            {/* <Route path="/following-companies" exact element={<FollowingCompanies />} /> */}
            {/* <Route path="/wishlist-properties" exact element={<WhistlistProperties />} /> */}
            {/* <Route path="/RequestPackage" exact element={<BecomeAPartner />} /> */}

            {/* <Route path="/add-event" exact element={<AddOrEditEventForm />} />
            <Route path="/add-property" exact element={<AddOrEditPropertyForm />} />
            <Route path="/add-project" exact element={<AddOrEditProject />} />
            <Route path="/add-agent" exact element={<AddOrEditAgent />} /> */}
            {/* <Route path="/agent/manage" exact element={<CreateAgentPage />} /> */}
            {/*  */}
            {/* User Dashboard */}

            <Route path="/" element={<ProtectedRoute allowedRole={['user']} />}>
              <Route path="account-settings" exact element={<AccountSettings />} />
              <Route path="followed-agents" exact element={<FollowedAgents />} />
              <Route path="compare-list" exact element={<CompareList />} />
              <Route
                path="saved-properties"
                exact
                element={<SavedProperties key={'saved-properties'} />}
              />
              <Route path="saved-events" exact element={<SavedProperties key={'saved-events'} />} />
              <Route path="property-requests" exact element={<UserPropertyRequests />} />
              <Route path="contacted-properties" exact element={<UserContactedProperties />} />
              <Route
                path="saved-projects"
                exact
                element={<SavedProperties key={'saved-projects'} />}
              />
              <Route path="saved-searches" exact element={<SavedSearches />} />
            </Route>

            <Route path="/notifications" exact element={<NotificationPage />} />
          </Routes>
          <FloatingChatBot />
        </Suspense>
      </BrowserRouter>
    </React.Fragment>
  );
}

export default App;
